import { useState, useCallback } from 'react'
import * as Sentry from '@sentry/react'
import { Dropdown, App, Tooltip } from 'antd'
import type { MenuProps } from 'antd'
import {
  Copy,
  Trash2,
  Pencil,
  Download,
  Clipboard,
  Info,
  HelpCircle,
  Minus,
  Plus,
  FileOutput,
} from 'lucide-react'
import { formatNodeContent } from '../../../utils/nodeFormatter'
import type { ReactFlowNodeData } from '../../../types/mindmap-components'
import { StyledButton, StyledMoreIcon } from './styles/MoreOptionsButtonStyles'
import type { IdeationPhase, BranchType } from '@bandos/shared'
import { FONT_WEIGHT, TYPOGRAPHY } from '../../../styles/theme'
import {
  IDEATION_PHASE_NAMES,
  IDEATION_PHASE_DESCRIPTIONS,
  NON_UI_PHASE_NAMES,
  NON_UI_PHASE_DESCRIPTIONS,
} from '@bandos/shared'

const TOTAL_PHASES = 6

interface MoreOptionsButtonProps {
  nodeData?: ReactFlowNodeData
  nodeId?: string
  isRootNode?: boolean
  onDelete?: (nodeId: string) => void // Deletes node and all children
  onEdit?: () => void // Opens edit mode (e.g., for storyboards)
  onDownloadPNG?: () => void // Download as PNG (e.g., for storyboards)
  onCopyToClipboard?: () => void // Copy to clipboard (e.g., for storyboards)
  size?: number
  className?: string
  onCopy?: () => Promise<void> | void // Optional custom copy handler
  hideCopy?: boolean // If true, hides the copy option
  phase?: IdeationPhase // If provided, adds a Metadata submenu showing maturity level
  branchType?: BranchType // UI_DIGITAL or NON_UI classification for phase 3 nodes
  branchReasoning?: string // One-sentence explanation of the classification
  onNavigateToReport?: () => void // Navigate to the export/report page for this node's thread
  onCollapse?: () => void // Collapse this node's children
  onExpand?: () => void // Expand a collapsed node
}

/**
 * Reusable more options button component (triple dot menu)
 * Gray icon by default, darker on hover
 * Opens a dropdown menu with node actions
 */
export function MoreOptionsButton({
  nodeData,
  nodeId,
  isRootNode = false,
  onDelete,
  onEdit,
  onDownloadPNG,
  onCopyToClipboard,
  size = 10,
  className,
  onCopy,
  hideCopy = false,
  onNavigateToReport,
  phase,
  branchType,
  branchReasoning,
  onCollapse,
  onExpand,
}: MoreOptionsButtonProps) {
  const [isHovered, setIsHovered] = useState(false)
  const { notification: antdNotification } = App.useApp()

  const handleCopyContent = useCallback(async () => {
    // Use custom copy handler if provided
    if (onCopy) {
      await onCopy()
      return
    }
    // Default copy behavior using nodeData
    if (!nodeData) return
    try {
      const formattedText = formatNodeContent(nodeData)
      await navigator.clipboard.writeText(formattedText)
      antdNotification.success({
        title: 'Copied to clipboard',
        description: 'Node content has been copied to your clipboard.',
        duration: 2,
      })
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'mindmap', action: 'copy_node_content' },
      })
      antdNotification.error({
        title: 'Failed to copy',
        description: 'Could not copy text to clipboard. Please try again.',
        duration: 3,
      })
    }
  }, [nodeData, antdNotification, onCopy])

  const handleDelete = useCallback(() => {
    if (nodeId && onDelete) {
      onDelete(nodeId)
    }
  }, [nodeId, onDelete])

  // Build menu items dynamically based on props
  const menuItems: MenuProps['items'] = [
    // Edit option (e.g., for storyboards)
    ...(onEdit
      ? [
          {
            key: 'edit',
            label: 'Edit',
            icon: <Pencil size={14} />,
            onClick: onEdit,
          },
        ]
      : []),
    // Export options (e.g., for storyboards)
    ...(onDownloadPNG
      ? [
          {
            key: 'download-png',
            label: 'Download as PNG',
            icon: <Download size={14} />,
            onClick: onDownloadPNG,
          },
        ]
      : []),
    ...(onCopyToClipboard
      ? [
          {
            key: 'copy-clipboard',
            label: 'Copy to Clipboard',
            icon: <Clipboard size={14} />,
            onClick: onCopyToClipboard,
          },
        ]
      : []),
    // Divider after edit/export options
    ...(onEdit || onDownloadPNG || onCopyToClipboard
      ? [
          {
            type: 'divider' as const,
            key: 'divider-edit',
          },
        ]
      : []),
    // Delete option (only if not root node and callback provided)
    ...(!isRootNode && nodeId && onDelete
      ? [
          {
            key: 'delete',
            label: 'Delete',
            icon: <Trash2 size={14} />,
            onClick: handleDelete,
            danger: true,
          },
          {
            type: 'divider' as const,
            key: 'divider-1',
          },
        ]
      : []),
    // Copy option (hidden for nodes without text content like storyboards)
    ...(!hideCopy
      ? [
          {
            key: 'copy',
            label: 'Copy text content',
            icon: <Copy size={14} />,
            onClick: handleCopyContent,
          },
        ]
      : []),
    // Export thread option
    ...(onNavigateToReport
      ? [
          {
            key: 'export-thread',
            label: 'Export thread',
            icon: <FileOutput size={14} />,
            onClick: () => {
              // Defer so the dropdown unmounts cleanly before navigation
              requestAnimationFrame(() => onNavigateToReport())
            },
          },
        ]
      : []),
    // Collapse option (only shown for nodes with children)
    ...(onCollapse
      ? [
          {
            key: 'collapse',
            label: 'Collapse',
            icon: <Minus size={14} />,
            onClick: () => {
              // Defer so the dropdown unmounts cleanly before the node type changes
              requestAnimationFrame(() => onCollapse())
            },
          },
        ]
      : []),
    // Expand option (only shown for collapsed/summarized nodes)
    ...(onExpand
      ? [
          {
            key: 'expand',
            label: 'Expand',
            icon: <Plus size={14} />,
            onClick: () => {
              requestAnimationFrame(() => onExpand())
            },
          },
        ]
      : []),
    // Metadata submenu (for idea nodes with a phase or branch type) — always at the bottom
    ...(phase !== undefined || branchType !== undefined
      ? [
          {
            type: 'divider' as const,
            key: 'divider-metadata',
          },
          {
            key: 'metadata',
            label: (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Info size={13} style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }} />
                Metadata
              </span>
            ),
            children: [
              ...(phase !== undefined
                ? [
                    {
                      key: 'maturity',
                      disabled: true,
                      label: (
                        <span
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            pointerEvents: 'none',
                          }}
                        >
                          <span
                            style={{
                              color: 'var(--color-text-secondary)',
                              fontSize: TYPOGRAPHY.caption.fontSize,
                            }}
                          >
                            Maturity
                          </span>
                          <span
                            style={{
                              fontWeight: FONT_WEIGHT.semibold,
                              fontSize: TYPOGRAPHY.caption.fontSize,
                            }}
                          >
                            {phase}&thinsp;/&thinsp;{TOTAL_PHASES}
                          </span>
                          <Tooltip
                            title={`${branchType === 'NON_UI' && phase >= 4 ? (NON_UI_PHASE_NAMES[phase] ?? IDEATION_PHASE_NAMES[phase]) : IDEATION_PHASE_NAMES[phase]}: ${branchType === 'NON_UI' && phase >= 4 ? (NON_UI_PHASE_DESCRIPTIONS[phase] ?? IDEATION_PHASE_DESCRIPTIONS[phase]) : IDEATION_PHASE_DESCRIPTIONS[phase]}`}
                            mouseEnterDelay={0.2}
                            placement="right"
                          >
                            <HelpCircle
                              size={12}
                              style={{
                                color: 'var(--color-text-disabled)',
                                pointerEvents: 'all',
                                cursor: 'default',
                                flexShrink: 0,
                              }}
                            />
                          </Tooltip>
                        </span>
                      ),
                    },
                  ]
                : []),
              ...(branchType !== undefined
                ? [
                    {
                      key: 'branch-type',
                      disabled: true,
                      label: (
                        <span
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            pointerEvents: 'none',
                          }}
                        >
                          <span
                            style={{
                              color: 'var(--color-text-secondary)',
                              fontSize: TYPOGRAPHY.caption.fontSize,
                            }}
                          >
                            Track
                          </span>
                          <span
                            style={{
                              fontWeight: FONT_WEIGHT.semibold,
                              fontSize: TYPOGRAPHY.caption.fontSize,
                              padding: '1px 6px',
                              borderRadius: 4,
                              background:
                                branchType === 'UI_DIGITAL'
                                  ? 'var(--color-primary-subtle, #e8f4ff)'
                                  : 'var(--color-success-subtle, #e8f5e9)',
                              color:
                                branchType === 'UI_DIGITAL'
                                  ? 'var(--color-primary, #1677ff)'
                                  : 'var(--color-success, #52c41a)',
                            }}
                          >
                            {branchType === 'UI_DIGITAL' ? 'UI / Digital' : 'Non-UI'}
                          </span>
                          {branchReasoning && (
                            <Tooltip
                              title={branchReasoning}
                              mouseEnterDelay={0.2}
                              placement="right"
                            >
                              <HelpCircle
                                size={12}
                                style={{
                                  color: 'var(--color-text-disabled)',
                                  pointerEvents: 'all',
                                  cursor: 'default',
                                  flexShrink: 0,
                                }}
                              />
                            </Tooltip>
                          )}
                        </span>
                      ),
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
  ]

  return (
    <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
      <StyledButton
        onClick={(e) => e.stopPropagation()}
        className={className}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <StyledMoreIcon size={size} $isHovered={isHovered} />
      </StyledButton>
    </Dropdown>
  )
}
